// LAP-733 (GE022) round 11 — translated + redesigned home-page mode picker.
// The previous version had hard-coded French copy and a row-on-row radio
// layout. We now ship:
//   * useTranslation('layout') so labels follow the active UI language;
//   * a card-based picker with a primary highlight on the selected option;
//   * a sticky footer matching the rest of the settings panes.
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Radio,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import GridViewIcon from '@mui/icons-material/GridView'
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { useForm } from 'react-hook-form'
import { makePostRequest } from 'utils/api'

const NAVY = '#001B42'
const ACCENT = '#2563EB'

export default function PageDAccueil({ appUser, handleClose, fetchUser }) {
  const { t } = useTranslation('layout')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [selected, setSelected] = useState('SmartFeed')

  useEffect(() => {
    setSelected(appUser?.user_parameter?.page_daccueil || 'SmartFeed')
  }, [appUser])

  const { handleSubmit } = useForm({ defaultValues: {} })

  const onSubmit = async () => {
    const payload = { page: selected }
    const response = await makePostRequest('/api/user/update-aspect/', payload, {}, true)
    if (response.status === 200) {
      fetchUser()
    }
  }

  const options = [
    {
      value: 'Tout',
      icon: <GridViewIcon fontSize="medium" />,
      label: t('settings.homePage.modeAll'),
      group: 'classic',
    },
    {
      value: 'Groupe',
      icon: <ViewAgendaIcon fontSize="medium" />,
      label: t('settings.homePage.modeGroup'),
      group: 'classic',
    },
    {
      value: 'SmartFeed',
      icon: <AutoAwesomeIcon fontSize="medium" />,
      label: t('settings.homePage.modeSmartFeed'),
      group: 'smartfeed',
    },
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
      <Box display="flex" flexDirection="column" height="100%" sx={{ bgcolor: '#F7F9FC' }}>
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #0A2A66 100%)`,
            color: 'white',
            p: isMobile ? 2 : 3,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('settings.homePage.title')}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ ml: 'auto', color: 'white' }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: isMobile ? 2 : 4, py: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settings.homePage.modeLabel')}
          </Typography>

          <Stack spacing={1.5}>
            {options.map((opt) => {
              const isSelected = selected === opt.value
              return (
                <Card
                  key={opt.value}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: isSelected ? ACCENT : '#E5E7EB',
                    boxShadow: isSelected ? '0 0 0 2px rgba(37,99,235,0.15)' : 'none',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  <CardActionArea onClick={() => setSelected(opt.value)}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            bgcolor: isSelected ? 'rgba(37,99,235,0.10)' : '#F1F5F9',
                            color: isSelected ? ACCENT : 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {opt.icon}
                        </Box>
                        <Typography sx={{ flex: 1, fontWeight: isSelected ? 600 : 500 }}>
                          {opt.label}
                        </Typography>
                        <Radio
                          checked={isSelected}
                          value={opt.value}
                          sx={{
                            color: '#94A3B8',
                            '&.Mui-checked': { color: ACCENT },
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              )
            })}
          </Stack>

          {/* Description cards */}
          <Stack spacing={2} sx={{ mt: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
              <CardContent>
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {t('settings.homePage.classicTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.homePage.classicDescription')}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
            <Card elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
              <CardContent>
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {t('settings.homePage.smartFeedTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.homePage.smartFeedDescription')}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        {/* Sticky footer */}
        <Box
          sx={{
            borderTop: '1px solid #E5E7EB',
            bgcolor: 'white',
            px: isMobile ? 2 : 3,
            py: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="text"
              onClick={handleClose}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {t('settings.homePage.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                bgcolor: ACCENT,
                '&:hover': { bgcolor: '#1D4ED8' },
                px: 3,
              }}
            >
              {t('settings.homePage.save')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </form>
  )
}

PageDAccueil.propTypes = {
  appUser: PropTypes.object,
  setAppUser: PropTypes.func,
  handleClose: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
}
